import HBPageTitle from "@/components/HBPageTitle";
import { getPinsByTag, getUserPins } from "@/services";

import { useMemoizedFn, useUpdateEffect } from "ahooks";

import HBSelect from "@/components/HBSelect";
import { useInfiniteList } from "@/utils/hooks/useInfiniteList";
import { checkCanUploadPin } from "@/utils/open";
import constantFactory from "@gaoding/constant-factory";
import classNames from "classnames";
import { last, omit } from "lodash";
import Link from "next/link";
import { useRouter } from "next/router";
import { useCallback, useMemo, useRef, useState } from "react";
import HBIcon from "../../HBIcon";
import HBMessage from "../../HBMessage";
import PinList from "../../PinList";
import styles from "../index.module.less";
import Empty from "./Empty";
import Loading from "./Loading";
import TabPanel from "./TabPanel";
import TagSelectModal from "./TagSelectModal";

export default function PinListPanel({
  user,
  isMe,
  initPins,
  showTitle = false,
  showCreated = false,
  hideCreateBtn = false,
  created = false,
}) {
  const router = useRouter();
  const { currentTab } = router.query;
  const [loading, setLoading] = useState(!!initPins);
  const maxRef = useRef(last(initPins));

  const queryFn = useMemoizedFn(async (page) => {
    const PAGE_SIZE = 30;
    let params;
    const { type, tag_name } = router.query;

    if (page === 1) {
      // 第一页，重置 max
      maxRef.current = null;
    }

    if (type || created) {
      // 有类型筛选条件的， 走搜索参数
      params = {
        username: user.urlname,
        sort: "created_at", // 默认按创建时间降序排序
        page,
        material: type === "material",
        ...(type !== "material" ? { category: type } : null),
      };

      // 我创建的
      if (created) {
        params.created = true;
      } else {
        delete params.created;
      }
    } else {
      params = {
        username: user.urlname,
        max: maxRef.current?.pin_id,
        limit: PAGE_SIZE,
      };
    }

    let res;

    setLoading(true);

    if (tag_name) {
      res = await getPinsByTag({
        urlname: user.urlname,
        tag_name: encodeURIComponent(tag_name),
        limit: PAGE_SIZE,
        max: maxRef.current?.seq,
      });
    } else {
      res = await getUserPins(params);
    }

    setLoading(false);

    maxRef.current = last(res?.pins);

    return res?.pins || [];
  });

  const {
    list: records,
    hasMore,
    onLoadMore,
    resetData,
  } = useInfiniteList(queryFn, {
    initialData: initPins,
  });

  // 排序类型列表
  const { SORT_LIST } = useMemo(() => {
    // 为了避免切换用户的情况
    return constantFactory("SORT", [
      {
        label: "筛选",
        type: "label",
      },
      {
        key: "label",
        label: "按标签筛选",
        value: "tag",
      },
    ]);
  }, []);

  // 我创建的 切换时，重置采集列表
  // 切换用户时，重置采集列表
  useUpdateEffect(() => {
    resetData();
  }, [user?.user_id, created, resetData, router.query?.tag_name]);

  // 点击创建采集，显示图片上传弹窗
  const onCreateClick = useCallback(async () => {
    if (!checkCanUploadPin(user)) {
      return;
    }
    const { PinUploaderModal } = await import("@/components/PinCreator");

    PinUploaderModal.open({
      name: "pinUploaderModal",
      onOk: async () => {
        HBMessage.success("添加成功");
        resetData();
      },
    });
  }, [user, resetData]);

  // 瀑布流第一个位置的，创建采集卡片
  const createBtn = useMemo(
    () => (
      <div
        className={styles.createRect}
        key={"createBtn"}
        onClick={onCreateClick}
        data-gd-click="button_click"
        data-button-name="添加采集"
        title="上传采集"
      >
        <div className={styles.block}>
          <HBIcon type={"plus"} className={styles.icon} />
        </div>
      </div>
    ),
    [onCreateClick]
  );

  const onFilterChange = useMemoizedFn(() => {
    TagSelectModal.open({
      urlname: user.urlname,
      onOk: (tag) => {
        router.push({
          query: {
            ...router.query,
            tag_name: tag[0],
          },
        });
      },
    });
  });

  // 显示 创建的 按钮
  const actions = useMemo(() => {
    if (!showCreated) return null; // 不显示创建的按钮
    return (
      <div className={styles.actionsContent}>
        <div
          className={classNames(styles.createdBtn, {
            [styles.active]: currentTab === "pins",
          })}
        >
          <Link href="/space/pins">
            <a data-gd-click="button_click" data-button-name="全部灵感">
              {" "}
              全部{" "}
            </a>
          </Link>
        </div>
        <div
          className={classNames(styles.createdBtn, {
            [styles.active]: currentTab === "created",
          })}
          id="user_page_guide_5"
        >
          <Link href="/space/created">
            <a data-gd-click="button_click" data-button-name="我创建的">
              {" "}
              我创建的{" "}
            </a>
          </Link>
        </div>

        <div
          className={styles.filter}
          data-gd-click="button_click"
          data-button-name="标签"
        >
          {router.query?.tag_name ? (
            <div className={styles.filterName}>
              {router.query?.tag_name}
              <HBIcon
                type="ic_clear"
                className={styles.closeIcon}
                onClick={() => {
                  router.push({
                    query: omit(router.query, ["tag_name"]),
                  });
                }}
              />
            </div>
          ) : null}
          <HBSelect
            id="user_page_guide_6"
            allowClear={false}
            options={SORT_LIST}
            onChange={onFilterChange}
          />
        </div>
      </div>
    );
  }, [showCreated, currentTab, router, SORT_LIST, onFilterChange]);

  return (
    <>
      {/* 显示标题: showTitle = true */}
      {showTitle && (
        <HBPageTitle
          title={`${isMe ? "我" : user?.username}全部采集的`}
          backAction={() => {
            router.back();
          }}
        />
      )}

      <TabPanel actions={actions}>
        {loading && !records.length ? (
          <Loading height={260} />
        ) : (
          <div style={{ marginTop: "16px" }}>
            {records.length ? (
              <PinList
                dataSource={records}
                onLoadMore={onLoadMore}
                hasMore={hasMore}
                hideUser
                hideCount={!isMe}
              >
                {isMe && !hideCreateBtn ? createBtn : null}
              </PinList>
            ) : isMe && !hideCreateBtn ? (
              <>
                {createBtn}
                <div className={styles.empty}>还没有任何内容～</div>
              </>
            ) : (
              <Empty
                imageUrl={
                  "https://cdn.dancf.com/fe-assets/img/e66686c56220ce789c966b6653936017.png"
                }
                title={`${isMe ? "您" : "Ta"}还没有上传任何内容～`}
              />
            )}
          </div>
        )}
      </TabPanel>
    </>
  );
}
